import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Stack } from '@mui/material';
import { LegalPerson, IdentityCheckFlowLegalPerson } from '~/api/types';
import { DevelopmentSelectionButton } from '~/components/button/development-selection-button';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';
import config from '~/config';

export function DevelopmentSelectionScreen<T extends LegalPerson | IdentityCheckFlowLegalPerson>({
  person,
  onDevelopmentSelection,
  goBack,
  title,
}: DevelopmentSelectionScreenProps<T>) {
  const CURRENT_CUB_MAGIC_LINK = window.location.host;
  const IS_WHITE_LABEL = config.CUB_HOST !== CURRENT_CUB_MAGIC_LINK;

  const filteredDevelopments = person.developments
    .filter((d) => {
      // always show all contracts for non-white-label deployments
      if (!IS_WHITE_LABEL) return true;

      // show only contracts allowed of current magic link white-label
      const sanitizedMagicLinkUrl = d.magicLinkUrl?.replace('https://', '').replace('/', '');
      if (sanitizedMagicLinkUrl === CURRENT_CUB_MAGIC_LINK) return true;

      return false;
    })
    .sort((a, b) => a.companyName.localeCompare(b.companyName));

  const hasMultipleDevelopments = filteredDevelopments.length > 1;

  const message = hasMultipleDevelopments
    ? 'Encontramos mais de um contrato no seu nome. Selecione uma opção para continuar:'
    : 'Selecione uma opção para continuar:';

  if (filteredDevelopments.length === 0)
    return (
      <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
        <Title>Nenhum empreendimento encontrado</Title>
        <Box width={'444px'}>
          <Body1>Verifique os dados informados ou entre em contato com o atendimento</Body1>
        </Box>
        <Stack gap={2} mt={3} alignItems={'center'}>
          <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
            Voltar
          </Button>
        </Stack>
      </Page>
    );

  return (
    <Page>
      <Title>{title}</Title>
      <Box width={{ xs: '100%' }}>
        <Body1>{message}</Body1>
      </Box>
      <Box>
        {filteredDevelopments.map((development, idx) => (
          <DevelopmentSelectionButton
            key={idx}
            development={development}
            onClickWhenUrlIsNull={() => onDevelopmentSelection(development)}
          />
        ))}
      </Box>
      <Stack gap={2} mt={3} alignItems={'center'}>
        <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
          Voltar
        </Button>
      </Stack>
    </Page>
  );
}

interface DevelopmentSelectionScreenProps<T extends LegalPerson | IdentityCheckFlowLegalPerson> {
  person: T;
  onDevelopmentSelection: (customer: T['developments'][number]) => void;
  goBack: () => void;
  title: string;
}
